import {DateTime} from 'luxon';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Loader} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../../srcCommon/formatters/dateTime/relativeDateTimeFormatter';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  getAnalyticsReportErrors,
  getAnalyticsReportHasStarted,
  getAnalyticsReportLastUpdated,
  getAnalyticsReportProgress,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {ReportHeaderBusinessHours} from './reportHeaderBusinessHours';

/*
 * Props.
 */

interface ReportHeaderTitleRowProps {
  title: ReactNode;
}

/*
 * Styles.
 */

const TextFlex = styled.div`
  display: flex;
  align-items: baseline;
`;

const StyledFlex = styled.div`
  ${flexCenter};
`;

const StyledTitleRowWrapper = styled(StyledFlex)`
  justify-content: space-between;
  margin-bottom: 27px;
`;

const StyledReportTitle = styled.div`
  margin-right: 16px;

  color: ${(p) => p.theme.greys.shade90};

  // Not supported in fontSizes currently
  font-size: 24px;
  // Not supported in fontWeights currently
  font-weight: 800;
`;

const StyledLastUpdated = styled.div`
  font-size: ${fontSizes.medium};

  color: ${(p) => p.theme.greys.shade60};
  margin-right: 16px;
`;

const StyledProgress = styled(StyledLastUpdated)`
  ${flexCenter}

  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.interactiveText};
`;

const StyledBusinessHours = styled(StyledFlex)`
  margin-right: 10px;
`;

const LoaderWrapper = styled.div`
  margin-right: 9px;
  height: fit-content;
`;

/*
 * Component.
 */

export const ReportHeaderTitleRow: FC<ReportHeaderTitleRowProps> = ({title}) => {
  const reportProgress = useAppSelector(getAnalyticsReportProgress);
  const reportHasStarted = useAppSelector(getAnalyticsReportHasStarted);
  const reportLastUpdated = useAppSelector(getAnalyticsReportLastUpdated);
  const errors = useAppSelector(getAnalyticsReportErrors);

  return (
    <StyledTitleRowWrapper>
      <TextFlex>
        <StyledReportTitle>{title}</StyledReportTitle>
        {maybeRenderLoader(errors, reportProgress, reportHasStarted, reportLastUpdated)}
      </TextFlex>
      <StyledFlex>
        <StyledBusinessHours>
          <ReportHeaderBusinessHours />
        </StyledBusinessHours>
      </StyledFlex>
    </StyledTitleRowWrapper>
  );
};

/*
 * Helpers.
 */

function maybeRenderLoader(errors: string[], progress: number, hasStarted: boolean, lastUpdated?: DateTime) {
  if (errors.length) {
    return null;
  }

  if (progress < 100) {
    return (
      <StyledProgress>
        <LoaderWrapper>
          <Loader size={VisualSizesEnum.SMALL} isEnabled />
        </LoaderWrapper>
        {hasStarted ? (
          <Trans id="analytics-header-loader-progress-numeric">
            Progress: {computeJobProgress(progress).toFixed(0)}%
          </Trans>
        ) : (
          <Trans id="analytics-header-loader-progress-request-sent">Fetching report</Trans>
        )}
      </StyledProgress>
    );
  }

  if (lastUpdated) {
    return (
      <StyledLastUpdated>
        <Trans id="analytics-header-last-updated">Last updated</Trans>{' '}
        <RelativeDateTimeFormatter value={lastUpdated.valueOf()} format={RelativeDateTimeFormatsEnum.FULL} />
      </StyledLastUpdated>
    );
  }

  return null;
}

// Show at least 5% progress to the user (Progress will go from 5% to 100%)
function computeJobProgress(progress: number) {
  return 5 + progress * 0.95;
}
